import { mapType, SearchOption } from "@/models/base/SearchOption";
import {
  SendMessageContextFilter,
  WorkspaceContext,
} from "@/services/messageService";
import { WorkspaceFolder } from "@/models/workspace/Workspace";
import { DataSource, normalizeDataSource } from "@/config/datasources";

/**
 * Convert a source string to its API representation.
 * Uses central datasource normalization.
 */
export function stringToSearchOptionType(source: string): string | undefined {
  const normalized = normalizeDataSource(source);
  if (!normalized) return undefined;

  // Map to API representation using mapType
  return mapType(normalized);
}

function convertFiltersToSearchOptions(
  selectedSource: string | undefined,
  workspaceContext: WorkspaceContext
): SearchOption[] {
  const searchOptions: SearchOption[] = [];

  if (!selectedSource && Object.keys(workspaceContext).length === 0) {
    return [
      {
        type: mapType(DataSource.ChatGPT),
        filter: "",
      },
    ];
  }

  // handle workspace context
  if (Object.keys(workspaceContext).length > 0) {
    searchOptions.push({
      type: DataSource.Workspace,
      filter: "",
    });
  }

  if (selectedSource) {
    const enumType = stringToSearchOptionType(selectedSource);
    if (enumType) {
      searchOptions.push({
        type: enumType,
        filter: "",
      });
    } else {
      searchOptions.push({
        type: selectedSource,
        filter: "",
      });
    }
  }

  return searchOptions;
}

function convertSearchOptionsToSource(
  searchOptions?: SearchOption[]
): string | undefined {
  if (!searchOptions) return undefined;

  for (const option of searchOptions) {
    const source = option.type;

    if (
      source === DataSource.ChatGPT ||
      source === DataSource.Workspace
    ) {
      continue;
    }

    // Return the first non-workspace, non-chatgpt source found
    return source;
  }

  return undefined;
}

function convertContextFiltersToWorkspaceContext(
  contextFilters: SendMessageContextFilter[]
): WorkspaceContext {
  const result: WorkspaceContext = {};

  for (const {
    workspaceId,
    documentIds,
    documentIdsWithRevisionNumbers: documentIdsWithRevisionNumber,
  } of contextFilters) {
    if (documentIds && documentIds.length > 0) {
      result[workspaceId] = documentIds.map((id) => ({
        documentId: id,
        activeRevisionNumber: 1,
      }));
    } else if (
      documentIdsWithRevisionNumber &&
      documentIdsWithRevisionNumber.length > 0
    ) {
      result[workspaceId] = documentIdsWithRevisionNumber;
    } else {
      continue;
    }
  }

  return result;
}

const collectFolderAndDocumentIds = (folders: WorkspaceFolder[]): string[] => {
  const ids: string[] = [];
  for (const folder of folders) {
    ids.push(folder.id);
    ids.push(...folder.documents.map((d) => d.id));
    ids.push(...collectFolderAndDocumentIds(folder.folders));
  }
  return ids;
};

export {
  convertFiltersToSearchOptions,
  convertSearchOptionsToSource,
  convertContextFiltersToWorkspaceContext,
  collectFolderAndDocumentIds,
};
